import { useState, useEffect, useMemo, useCallback } from "react";
import { useIntegrationProviders } from "@/features/integrations_provider/hooks/useIntegrationProviders";
import { useAccountManager } from "@/features/account_manager/hooks/useAccountManager";
import { useIntegrationAccess } from "../hooks/useIntegrationAccess";
import type IntegrationAccess from "../models/IntegrationAccess";
import type IntegrationProvider from "@/features/integrations_provider/models/integrationProvider";
import type { UserProfile } from "@/features/account_manager/models/UserProfile";
import { Shield, Check, Minus, Plus, Trash2, Loader2, Search } from "lucide-react";
import GlassContainer from "@/shared/components/glasscontainer";
import { confirmDialog } from "@/shared/components/dialog";
import GlassBackground from "@/shared/components/glassBackground";
import BackButton from "@/shared/components/backButton";
import NoIcon from "@/assets/icons/no_icon.svg";

/** Cell key for the pending-request set and the access lookup. */
const cellKey = (userId: string, providerId: string) => `${userId}|${providerId}`;

export default function AccessManagementPage() {
  const [accessList, setAccessList] = useState<IntegrationAccess[]>([]);
  const [providers, setProviders] = useState<IntegrationProvider[]>([]);
  const [childUsers, setChildUsers] = useState<UserProfile[]>([]);
  const [me, setMe] = useState<UserProfile | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const [pendingCells, setPendingCells] = useState<Set<string>>(new Set());

  const { loading: providersLoading, getAll } = useIntegrationProviders();
  const { loading: accountsLoading, getPage, getMe } = useAccountManager();
  const {
    loading: accessLoading,
    error,
    getAll: getAllAccess,
    grant,
    revoke,
    update,
  } = useIntegrationAccess();

  const loading = providersLoading || accountsLoading || accessLoading;

  // Fetch data
  useEffect(() => {
    let ignore = false;
    const fetch = async () => {
      const [accessData, providersPage, meData] = await Promise.all([
        getAllAccess(),
        getAll({ pageNumber: 1, pageSize: 100 }),
        getMe(),
      ]);
      if (ignore) return;
      if (accessData) setAccessList(accessData);
      if (providersPage) setProviders(providersPage.items);
      if (meData) setMe(meData);
    };
    fetch();
    return () => { ignore = true; };
  }, [getAllAccess, getAll, getMe]);

  // Fetch child users after we know who "me" is
  useEffect(() => {
    if (!me) return;
    let ignore = false;
    const fetchAccounts = async () => {
      const page = await getPage({ pageNumber: 1, pageSize: 1000 });
      if (!ignore && page) {
        const children = page.items.filter((u) => u.parentId === me.id);
        setChildUsers(children);
      }
    };
    fetchAccounts();
    return () => { ignore = true; };
  }, [me, getPage]);

  /** (userId, providerId) → access record, for O(1) cell lookup. */
  const accessMap = useMemo(() => {
    const map = new Map<string, IntegrationAccess>();
    accessList.forEach((a) => map.set(cellKey(a.grantedToUserId, a.integrationProviderId), a));
    return map;
  }, [accessList]);

  const visibleUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return childUsers;
    return childUsers.filter((u) => (u.name || u.email).toLowerCase().includes(q));
  }, [childUsers, userQuery]);

  const withPending = useCallback(
    async (key: string, action: () => Promise<void>) => {
      setPendingCells((prev) => new Set(prev).add(key));
      try {
        await action();
      } catch {
        // error toast handled by useAsync
      } finally {
        setPendingCells((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [],
  );

  /** Empty cell → grant; existing cell → toggle enabled/disabled. */
  const handleCellClick = (userId: string, providerId: string) => {
    const key = cellKey(userId, providerId);
    if (pendingCells.has(key)) return;
    const existing = accessMap.get(key);

    if (!existing) {
      void withPending(key, async () => {
        const created = await grant({
          IntegrationProviderId: providerId,
          GrantedToUserId: userId,
        });
        if (created) setAccessList((prev) => [...prev, created]);
      });
      return;
    }

    void withPending(key, async () => {
      await update(existing.id, !existing.enabled);
      setAccessList((prev) =>
        prev.map((a) => (a.id === existing.id ? { ...a, enabled: !a.enabled } : a)),
      );
    });
  };

  const handleRevoke = async (item: IntegrationAccess, providerName: string, userName: string) => {
    const confirmed = await confirmDialog({
      title: "Revocar acceso",
      message: `¿Revocar el acceso de ${userName} a ${providerName}? Perderá la conexión al proveedor.`,
      confirmLabel: "Sí, revocar",
      cancelLabel: "Cancelar",
      variant: "danger",
    });
    if (!confirmed) return;
    const key = cellKey(item.grantedToUserId, item.integrationProviderId);
    void withPending(key, async () => {
      await revoke(item.id);
      setAccessList((prev) => prev.filter((a) => a.id !== item.id));
    });
  };

  const activeCount = accessList.filter((a) => a.enabled).length;

  return (
    <GlassBackground>
      <div className="relative flex-1 overflow-auto p-8">
        <div className="max-w-6xl mx-auto">
          <BackButton text={"Volver a integraciones"} link={"/admin/integrations"}>
            <h2 className="text-[40px] font-bold tracking-tight text-foreground">
              Accesos a integraciones
            </h2>
            <p className="text-foreground/50 text-lg font-medium mt-1">
              Un clic en la celda otorga el acceso; otro clic lo deshabilita o reactiva.
            </p>
          </BackButton>

          {error && (
            <GlassContainer className="p-4 mb-6 border-l-4 border-red-500 bg-red-500/5">
              <p className="text-red-700 font-medium">Error</p>
              <p className="text-red-600/80 text-sm">{error.message}</p>
            </GlassContainer>
          )}

          {loading && childUsers.length === 0 ? (
            <GlassContainer className="h-64 animate-pulse bg-white/20" />
          ) : childUsers.length === 0 ? (
            <GlassContainer className="p-12 flex flex-col items-center justify-center text-center">
              <Shield className="size-12 text-foreground/20 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Sin usuarios hijos
              </h3>
              <p className="text-sm text-foreground/50">
                No tienes usuarios hijos registrados. Crea uno primero en Cuentas.
              </p>
            </GlassContainer>
          ) : (
            <GlassContainer className="overflow-hidden">
              <div className="flex items-center justify-between gap-4 p-4 border-b border-black/5">
                <div className="relative w-64">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-foreground/30 pointer-events-none" />
                  <input
                    type="text"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    placeholder="Buscar usuario..."
                    className="w-full pl-8 pr-3 py-2 rounded-xl bg-white/70 border border-black/5 text-sm text-foreground placeholder:text-foreground/30 outline-none focus:border-accent/40 transition-colors"
                  />
                </div>
                <p className="text-xs text-foreground/40 font-medium shrink-0">
                  {activeCount} {activeCount === 1 ? "acceso activo" : "accesos activos"} ·{" "}
                  {childUsers.length} {childUsers.length === 1 ? "usuario" : "usuarios"} ·{" "}
                  {providers.length} {providers.length === 1 ? "proveedor" : "proveedores"}
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="sticky left-0 z-10 bg-white/70 backdrop-blur-md text-left text-[11px] font-bold uppercase tracking-wider text-foreground/40 px-4 py-3 border-b border-r border-black/5 min-w-44">
                        Usuario
                      </th>
                      {providers.map((provider) => (
                        <th
                          key={provider.id}
                          className="px-2 py-3 border-b border-black/5 min-w-24 max-w-28"
                          title={provider.name}
                        >
                          <div className="flex flex-col items-center gap-1.5">
                            <span className="flex size-8 items-center justify-center rounded-lg bg-white shadow-sm border border-black/5 overflow-hidden">
                              <img
                                src={provider.icon || NoIcon}
                                alt=""
                                className="size-5 object-contain"
                              />
                            </span>
                            <span className="text-[10px] font-semibold text-foreground/60 truncate max-w-24">
                              {provider.name}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleUsers.map((user) => {
                      const userName = user.name || user.email;
                      return (
                        <tr key={user.id} className="group/row hover:bg-accent/[0.03] transition-colors">
                          <td className="sticky left-0 z-10 bg-white/70 backdrop-blur-md px-4 py-2 border-b border-r border-black/5">
                            <p className="text-sm font-semibold text-foreground truncate max-w-40" title={userName}>
                              {userName}
                            </p>
                            {user.name && (
                              <p className="text-[11px] text-foreground/40 truncate max-w-40">{user.email}</p>
                            )}
                          </td>
                          {providers.map((provider) => {
                            const key = cellKey(user.id, provider.id);
                            const access = accessMap.get(key);
                            const pending = pendingCells.has(key);
                            return (
                              <td key={provider.id} className="group/cell px-2 py-2 border-b border-black/5 text-center">
                                <div className="relative inline-flex">
                                  <button
                                    onClick={() => handleCellClick(user.id, provider.id)}
                                    disabled={pending}
                                    title={
                                      !access
                                        ? `Otorgar ${provider.name} a ${userName}`
                                        : access.enabled
                                          ? "Activo — clic para deshabilitar"
                                          : "Deshabilitado — clic para reactivar"
                                    }
                                    className={`size-8 rounded-lg flex items-center justify-center transition-all cursor-pointer disabled:cursor-wait ${
                                      !access
                                        ? "border border-dashed border-black/10 text-transparent hover:border-accent/40 hover:text-accent hover:bg-accent/5"
                                        : access.enabled
                                          ? "bg-emerald-500/15 text-emerald-600 border border-emerald-500/20 hover:bg-emerald-500/25"
                                          : "bg-gray-500/10 text-gray-500 border border-gray-500/15 hover:bg-gray-500/20"
                                    }`}
                                  >
                                    {pending ? (
                                      <Loader2 className="size-4 animate-spin text-foreground/40" />
                                    ) : !access ? (
                                      <Plus className="size-4" />
                                    ) : access.enabled ? (
                                      <Check className="size-4" />
                                    ) : (
                                      <Minus className="size-4" />
                                    )}
                                  </button>
                                  {access && !pending && (
                                    <button
                                      onClick={() => handleRevoke(access, provider.name, userName)}
                                      title="Revocar acceso"
                                      className="absolute -top-2 -right-2 size-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md opacity-0 group-hover/cell:opacity-100 focus-visible:opacity-100 hover:bg-red-600 hover:scale-110 transition-all cursor-pointer"
                                    >
                                      <Trash2 className="size-3" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    {visibleUsers.length === 0 && (
                      <tr>
                        <td
                          colSpan={providers.length + 1}
                          className="px-4 py-8 text-center text-sm text-foreground/40"
                        >
                          Sin usuarios para "{userQuery.trim()}".
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-4 px-4 py-3 border-t border-black/5 text-[11px] text-foreground/40 font-medium">
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-4 rounded bg-emerald-500/15 border border-emerald-500/20 inline-flex items-center justify-center">
                    <Check className="size-3 text-emerald-600" />
                  </span>
                  Activo
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-4 rounded bg-gray-500/10 border border-gray-500/15 inline-flex items-center justify-center">
                    <Minus className="size-3 text-gray-500" />
                  </span>
                  Deshabilitado
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-4 rounded border border-dashed border-black/15" />
                  Sin acceso — clic para otorgar
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-4 rounded-full bg-red-500 inline-flex items-center justify-center">
                    <Trash2 className="size-2.5 text-white" />
                  </span>
                  Revocar — aparece al pasar el cursor sobre la celda
                </span>
              </div>
            </GlassContainer>
          )}
        </div>
      </div>
    </GlassBackground>
  );
}
